import React from 'react'

type LoadingBarContextValue = {
  start: () => void
  done: () => void
}

const LoadingBarContext = React.createContext<LoadingBarContextValue | null>(
  null
)

// Hook lives with context for provider scope
// eslint-disable-next-line react-refresh/only-export-components -- context + hook in one file is standard
export function useLoadingBar(): LoadingBarContextValue {
  const ctx = React.useContext(LoadingBarContext)
  if (!ctx) {
    return {
      start: () => {},
      done: () => {},
    }
  }
  return ctx
}

export function LoadingBarProvider({
  children,
}: Readonly<{ children: React.ReactNode }>) {
  const [count, setCount] = React.useState(0)
  const value = React.useMemo(
    () => ({
      start: () => setCount(c => c + 1),
      done: () => setCount(c => Math.max(0, c - 1)),
    }),
    []
  )
  return (
    <LoadingBarContext.Provider value={value}>
      {children}
      <LoadingBar visible={count > 0} />
    </LoadingBarContext.Provider>
  )
}

function LoadingBar({ visible }: Readonly<{ visible: boolean }>) {
  return (
    <div
      className={`fixed top-0 left-0 right-0 z-[9999] h-1 bg-blue-100 overflow-hidden pointer-events-none transition-opacity duration-300 ${
        visible ? 'opacity-100' : 'opacity-0'
      }`}
    >
      {visible && (
        <>
          <div className='absolute top-0 bottom-0 left-0 bg-gradient-to-r from-blue-600 to-indigo-600 loading-bar-primary-anim' />
          <div className='absolute top-0 bottom-0 left-0 bg-blue-400/50 loading-bar-secondary-anim' />
          <style>{`
            .loading-bar-primary-anim {
              width: 100%;
              animation: loading-bar-primary 2s infinite cubic-bezier(0.65, 0.815, 0.735, 0.395);
            }
            .loading-bar-secondary-anim {
              width: 100%;
              animation: loading-bar-secondary 2s infinite cubic-bezier(0.165, 0.84, 0.44, 1);
            }
            @keyframes loading-bar-primary {
              0% { left: -35%; width: 33%; }
              60% { left: 100%; width: 33%; }
              100% { left: 100%; width: 33%; }
            }
            @keyframes loading-bar-secondary {
              0% { left: -200%; width: 80%; }
              60% { left: 107%; width: 80%; }
              100% { left: 107%; width: 80%; }
            }
          `}</style>
        </>
      )}
    </div>
  )
}
